import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import type { AdminProfile } from '../auth';

interface AdminUserRecord {
  email: string;
  full_name: string;
  picture_url: string;
  role: string;
  status: string;
  created_at: string | null;
  last_login_at: string | null;
  created_by: string;
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export default function Admins({ admin }: { admin: AdminProfile }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admins'],
    queryFn: () => api.get<{ admins: AdminUserRecord[] }>('/api/admins'),
  });

  // Add form
  const [a_email, set_a_email] = useState('');
  const [a_name, set_a_name] = useState('');
  const [a_busy, set_a_busy] = useState(false);
  const [a_error, set_a_error] = useState<string | null>(null);

  async function handle_add(e: FormEvent) {
    e.preventDefault();
    set_a_busy(true);
    set_a_error(null);
    try {
      await api.post('/api/admins', {
        email: a_email.trim(),
        full_name: a_name.trim(),
      });
      qc.invalidateQueries({ queryKey: ['admins'] });
      set_a_email('');
      set_a_name('');
    } catch (err) {
      set_a_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_a_busy(false);
    }
  }

  async function handle_deactivate(email: string) {
    if (!confirm(`Deactivate ${email}?`)) return;
    try {
      await api.post('/api/admins/deactivate', { email });
      qc.invalidateQueries({ queryKey: ['admins'] });
    } catch (err) {
      alert(`Failed: ${err instanceof ApiError ? err.reason : String(err)}`);
    }
  }

  return (
    <div className="container">
      <h1>Admins</h1>
      <p className="muted" style={{ marginTop: -8, marginBottom: 24 }}>
        Anyone in this list with status <code>active</code> can sign in.
      </p>

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <DataTable
            rows={data.admins}
            columns={[
              { header: 'Email', cell: (a) => <strong>{a.email}</strong> },
              { header: 'Name', cell: (a) => a.full_name || '—' },
              { header: 'Role', cell: (a) => a.role },
              {
                header: 'Status',
                cell: (a) =>
                  a.status === 'active' ? (
                    <span style={{ color: 'var(--green-700)' }}>{a.status}</span>
                  ) : (
                    <span className="muted">{a.status}</span>
                  ),
              },
              { header: 'Last login', cell: (a) => format_dt(a.last_login_at) },
              { header: 'Created by', cell: (a) => a.created_by },
              {
                header: '',
                cell: (a) =>
                  a.email === admin.email ? (
                    <span className="muted" style={{ fontSize: 12 }}>(you)</span>
                  ) : a.status === 'active' ? (
                    <button
                      className="secondary"
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => handle_deactivate(a.email)}
                    >
                      Deactivate
                    </button>
                  ) : null,
              },
            ]}
            empty_message="No admins."
          />
        )}
      </div>

      <div className="card" style={{ maxWidth: 540 }}>
        <h2 style={{ marginTop: 0 }}>Add admin</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          New admin must have a <code>@captomi.com</code> Google Workspace account.
          Role is always <code>superadmin</code> in v1.
        </p>
        <form onSubmit={handle_add} style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Email</span>
            <input
              type="email"
              value={a_email}
              onChange={(e) => set_a_email(e.target.value)}
              required
              maxLength={254}
              placeholder="firstname.lastname@captomi.com"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Full name</span>
            <input
              value={a_name}
              onChange={(e) => set_a_name(e.target.value)}
              required
              maxLength={120}
              placeholder="Firstname Lastname"
            />
          </label>
          {a_error && <div className="error">{a_error}</div>}
          <div>
            <button type="submit" disabled={a_busy}>
              {a_busy ? 'Adding...' : 'Add admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
